import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createGlobalState, useTimeoutFn } from 'react-use';
import { useDropzone } from 'react-dropzone';

import { immutableSerialize } from 'app-components/utils/redux-store';
import { authSelectors } from 'app-components/flux/auth';
import { entitlementsActions, entitlementsSelectors } from 'app-components/flux/entitlements';
import { getLogger, tracker, getHostConfig } from 'app-components/utils/core';
import { coreActions } from 'app-components/flux/core-actions';
import { featureFlagsActions } from 'app-components/flux/feature-flags';
import { localPreferences } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import Link from '@mui/material/Link';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import darkPalette from 'themes/paletteDark';
import lightPalette from 'themes/paletteLight';
import * as sessionStorageEx from 'utils/sessionStorageEx';

import StdDialog from 'components/Dialogs/StdDialog';
import LoadingView from 'components/LoadingView';

import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { useFetchOnce } from 'app-components/hooks/utils';
import ZeroStateView from 'components/ZeroStateView';

import useAuthTokenValidator from './useAuthTokenValidator';
import { clearPendingMcpAuthorize, readPendingMcpAuthorize } from 'utils/mcp/pendingAuthorize';
import useAuthURL from './useAuthURL';
import useDatasetHandler from './useDatasetHandler';
import useEntitlementValidator from './useEntitlementValidator';
import useOAuthHandler from './useOAuthHandler';
/////////////////////////////////////////
import { getPreviewSignInUrl } from 'utils/devSso/previewTarget';
/////////

const logger = getLogger('components/Dialogs/AuthDialog');

export const useGlobalSuppressAuthDialog = createGlobalState(false);
export const useGlobalSandBox = createGlobalState(sessionStorageEx.getSandbox() || false);

const useStyles = makeStyles()((_theme, dm) => {
  const palette = dm ? darkPalette : lightPalette;

  return {
    paper: {
      backgroundColor: palette.background.paper,
      '@media (max-width: 500px)': {
        margin: 2,
      },
    },
    contentRoot: {
      backgroundColor: palette.background.paper,
      color: palette.text.primary,
      transition: 'all ease 500ms',
      '@media (max-width: 500px)': {
        padding: 1,
      },
    },
    loadingView: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      backgroundColor: palette.background.paper,
    },
  };
});

const AuthStates = Object.freeze({
  DIALOG_INACTIVE: 'DIALOG_INACTIVE',
  WIDGET_LOADING: 'WIDGET_LOADING',
  WIDGET_FAILED: 'WIDGET_FAILED',
  TRY_AGAIN: 'TRY_AGAIN',
  WIDGET_READY: 'WIDGET_READY',
  TOKEN_LOADING: 'TOKEN_LOADING',
  TOKEN_FAILED: 'TOKEN_FAILED',
  MANUAL_AUTH: 'MANUAL_AUTH',
});

const AuthDialog = React.memo((props) => {
  const { ...otherProps } = props;
  const dm = useTheme().palette.mode === 'dark' ? 1 : 0;

  const { classes } = useStyles(dm);

  const iframeRef = useRef();
  const [suppressAuthDialog] = useGlobalSuppressAuthDialog();
  const [, setSandbox] = useGlobalSandBox();
  const { accessToken } = useSelector(authSelectors.getAuthSession);
  const isLoadingToken = useSelector(authSelectors.getIsLoading);
  const authError = useSelector(authSelectors.getError);

  const [authState, setAuthState] = useState(AuthStates.DIALOG_INACTIVE);

  const authNotNeeded = false;

  useAuthTokenValidator({ authNotNeeded });
  useEntitlementValidator();
  useDatasetHandler({ authNotNeeded });
  const authURL = useAuthURL();
  useOAuthHandler();

  // Make sure subscriptions and entitlements are loaded. Both require authentication. Entitlements also require
  // a dataset ID to be set within the auth-session (to support dataset sharing).
  //
  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors, false, true);
  useFetchOnce(entitlementsActions.getEntitlements, entitlementsSelectors, false, false);

  const widgetLoadingTimeout = useCallback(() => {
    if (readPendingMcpAuthorize()) {
      logger.error('auth widget failed to load for an mcp enrollment, falling back to a normal sign-in');
      clearPendingMcpAuthorize();
      window.location.reload();
      return;
    }
    setAuthState(AuthStates.TRY_AGAIN);
  }, []);
  const [_isReady, widgetLoadingTimeoutCancel, widgetLoadingTimeoutReset] = useTimeoutFn(widgetLoadingTimeout, 30000);
  useEffect(() => {
    widgetLoadingTimeoutCancel();
  }, [widgetLoadingTimeoutCancel]);

  useEffect(() => {
    if (accessToken || authNotNeeded || suppressAuthDialog) {
      setAuthState(AuthStates.DIALOG_INACTIVE); // auth not required
    } else if (authState === AuthStates.DIALOG_INACTIVE) {
      if (getHostConfig()) {
        setAuthState(AuthStates.WIDGET_LOADING); // load widget
        widgetLoadingTimeoutReset();
      } else {
        setAuthState(AuthStates.MANUAL_AUTH); // unknown host
      }
    } else if (isLoadingToken) {
      setAuthState(AuthStates.TOKEN_LOADING); // show /token progress
    } else if (authError && authState === AuthStates.TOKEN_LOADING) {
      setAuthState(AuthStates.TOKEN_FAILED);
    }
  }, [accessToken, authNotNeeded, suppressAuthDialog, authError, authState, isLoadingToken, widgetLoadingTimeoutReset]);

  useEffect(() => {
    logger.log(`new auth widget state: ${authState}`);
    tracker.track(tracker.events.authWidget, { status: authState });
    if (authState === AuthStates.WIDGET_LOADING) {
      tracker.timeEvent(tracker.events.authWidget);
    }
  }, [authState]);

  const dialogProps = useMemo(() => {
    const palette = dm ? darkPalette : lightPalette;

    return {
      classes: { paper: classes.paper },
      BackdropProps: {
        sx: {
          backgroundColor: palette.backdrop.light,
          backdropFilter: palette.blur.hard,
          transition: 'all ease 500ms',
        },
      },
    };
  }, [classes.paper, dm]);

  const [manualAuthSession, setManualAuthSession] = useState('');
  let previewSignInUrl;
///////////////////////////////////////////
  // Amplify previews can sign in through the staging sender instead of a pasted auth object.
  previewSignInUrl = getPreviewSignInUrl();
///////////

  const dispatch = useDispatch();
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDropzone,
  } = useDropzone({
    accept: '.json',
    multiple: false,
    noClick: true,
    onDrop: (accepted) => {
      const file = accepted?.[0];
      if (file) {
        file?.text().then((json) => {
          const state = immutableSerialize.hydrateImmutable(json);
          dispatch(coreActions.setRoot(state));
          sessionStorageEx.setSandbox(true);
          setSandbox(true);
          localPreferences.setSuppressAssertPopups(true);
          setTimeout(() => {
            dispatch(
              featureFlagsActions.applyFeatureFlagsChanges({
                debug: false,
                dev: false,
              }),
            );
          }, 3000);
        });
      }
    },
  });

  if ([AuthStates.DIALOG_INACTIVE, AuthStates.TOKEN_LOADING].includes(authState)) return null;
  return (
    <>
      <StdDialog
        disableBackdropClick
        disableEscapeKeyDown
        id='manual-auth-dialog'
        open={authState === AuthStates.MANUAL_AUTH}
        showCloseButton={false}
      >
        <DialogContent className={classes.contentRoot} {...getRootProps()} sx={{ position: 'relative' }}>
          <input {...getInputProps()} />
          {isDragActive && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: (theme) => theme.zIndex.tooltip,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'action.hover',
                border: '4px dashed',
                borderColor: 'primary.main',
                borderRadius: 4,
                pointerEvents: 'none',
              }}
            >
              Drop state json file here
            </Box>
          )}
          <TextField
            autoFocus
            multiline
            fullWidth
            minRows={20}
            maxRows={20}
            variant='outlined'
            label='Authentication Required'
            placeholder='Paste your STG auth object here and click Submit to continue. Use "Dev Menu > Copy Auth to Clipboard" to copy auth object from another session.'
            onChange={(event) => setManualAuthSession(event.target.value)}
          />
          <br />
          <br />
          copy auth from&nbsp;&nbsp;
          <Link href='https://simplifi-stg.quickencs.com' target='_blank'>
            https://simplifi-stg.quickencs.com
            <OpenInNewRoundedIcon />
          </Link>
          <br />
          {'Use "Dev Menu > Copy Auth to Clipboard" to copy auth object from another session.'}
        </DialogContent>

        <DialogActions>
          {previewSignInUrl && (
            <Button variant='outlined' href={previewSignInUrl}>
              Sign in via staging
            </Button>
          )}
          <Button
            variant='outlined'
            onClick={() => {
              openDropzone();
            }}
          >
            Open State File
          </Button>

          <Button
            variant='contained'
            color='primary'
            disabled={!manualAuthSession.length}
            onClick={() => {
              sessionStorage.setItem('auth_session', manualAuthSession);
              window.location.reload();
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </StdDialog>

      <StdDialog
        disableBackdropClick
        disableEscapeKeyDown
        id='auth-dialog'
        open={authState !== AuthStates.DIALOG_INACTIVE && authState !== AuthStates.MANUAL_AUTH}
        showCloseButton={false}
        size='custom'
        {...dialogProps}
        {...otherProps}
      >
        <DialogContent className={classes.contentRoot}>
          <Box display='flex' flexDirection='column' position='relative'>
            {[AuthStates.WIDGET_LOADING, AuthStates.TOKEN_LOADING].includes(authState) && (
              <LoadingView className={classes.loadingView} />
            )}

            {AuthStates.TRY_AGAIN === authState && (
              <ZeroStateView
                title='Uh-oh!'
                description='We hit a snag. Please try again.'
                qIllustration='planeCrash'
                button='Try Again'
                onClick={() => {
                  setAuthState(AuthStates.DIALOG_INACTIVE);
                }}
                report='Auth widget load timeout.'
              />
            )}

            {[AuthStates.WIDGET_LOADING, AuthStates.WIDGET_READY, AuthStates.WIDGET_FAILED].includes(authState) && (
              <iframe
////////////////////////////////////////////////////////////
                allow='local-network-access' // allows auth widget to punt back to localhost
/////////////////////////
                id='login_frame'
                ref={iframeRef}
                style={{
                  opacity: authState === AuthStates.WIDGET_READY || AuthStates.WIDGET_FAILED ? 1 : 0,
                  transition: 'all 500ms',
                }}
                title='auth'
                aria-hidden='true'
                width={375}
                height={699}
                seamless
                loading='lazy'
                src={authURL?.href}
                onLoad={() => {
                  widgetLoadingTimeoutCancel();
                  if (authState !== AuthStates.WIDGET_READY) {
                    setAuthState(AuthStates.WIDGET_READY);
                    setTimeout(() => {
                      const isLoaded = Boolean(iframeRef.current?.contentWindow?.window?.length);
                      if (!isLoaded) {
                        setAuthState(AuthStates.WIDGET_FAILED);
                        // assert(false, 'auth widget load failed');
                      }
                    }, 3000);
                  }
                }}
              />
            )}

            {[AuthStates.TOKEN_LOADING, AuthStates.DIALOG_INACTIVE].includes(authState) && (
              <Box width={375} height={699} />
            )}

            {/* TODO: need design to handle errors */}
          </Box>
        </DialogContent>
      </StdDialog>
    </>
  );
});
AuthDialog.displayName = 'AuthDialog';
export default React.memo(AuthDialog);
